/**
 * Meta Pixel.
 *
 * Loaded only when VITE_META_PIXEL_ID is set, so development and self-hosted builds send nothing.
 *
 * The one rule that matters: CompleteRegistration fires ONLY after the server has confirmed an
 * account was created, and it carries the event_id the server generated. The Conversions API
 * sends the same event with the same id, and Meta deduplicates the pair. Firing on the button
 * click instead would count abandoned and failed signups as registrations and teach the lookalike
 * audience to find people who never finish.
 */
const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

declare global {
  interface Window { fbq?: any; _fbq?: any }
}

let ready = false;

export function initMetaPixel(): void {
  if (ready || !PIXEL_ID || typeof window === "undefined") return;
  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string, n?: any, t?: any, s?: any) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
    t = b.createElement(e) as HTMLScriptElement; t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode!.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", PIXEL_ID);
  ready = true;
}

function track(event: string, params?: Record<string, unknown>, opts?: { eventID?: string }): void {
  if (!PIXEL_ID || typeof window === "undefined" || !window.fbq) return;
  try {
    if (opts?.eventID) window.fbq("track", event, params ?? {}, { eventID: opts.eventID });
    else window.fbq("track", event, params ?? {});
  } catch {
    // Analytics must never break a page.
  }
}

function trackCustom(event: string, params?: Record<string, unknown>): void {
  if (!PIXEL_ID || typeof window === "undefined" || !window.fbq) return;
  try { window.fbq("trackCustom", event, params ?? {}); } catch {}
}

export function metaPageView(): void {
  track("PageView");
}

/** The signup page rendered. Diagnostic only; the campaign optimises on CompleteRegistration. */
export function metaViewContent(name: string): void {
  track("ViewContent", { content_name: name });
}

/** First real interaction with the signup form, fired once per page. */
let signupStarted = false;
export function metaSignupStart(): void {
  if (signupStarted) return;
  signupStarted = true;
  trackCustom("SignupStart");
}

/**
 * A new account exists. `eventId` MUST be the id the server generated and sent back, or Meta
 * cannot deduplicate against the Conversions API event and every signup is counted twice.
 *
 * Guarded so a refresh, a re-render, or a returning user resuming an old signup cannot fire it a
 * second time.
 */
const FIRED_KEY = "metaFiredRegistrations";
const firedRegistrations = new Set<string>();

/**
 * The in-memory Set alone only survives until the page reloads, and the campaign's own acceptance
 * test is "refreshing confirmation does not create another registration event". A parent who
 * refreshes the confirmation screen, or reopens it from history, must not be counted twice, so the
 * ids that have already fired are also kept in localStorage. Trimmed to the last 50 because this
 * is a browser that will only ever create a handful of accounts.
 */
function loadFired(): string[] {
  try {
    const raw = window.localStorage.getItem(FIRED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function metaCompleteRegistration(eventId: string | null | undefined): void {
  if (!eventId || typeof window === "undefined") return;
  if (firedRegistrations.has(eventId)) return;

  const persisted = loadFired();
  if (persisted.includes(eventId)) {
    firedRegistrations.add(eventId);
    return;
  }

  firedRegistrations.add(eventId);
  try {
    window.localStorage.setItem(FIRED_KEY, JSON.stringify([...persisted, eventId].slice(-50)));
  } catch {
    // A full or blocked localStorage must not stop the event; the in-memory guard still holds
    // for this page view, which is the common case.
  }
  track("CompleteRegistration", { status: true }, { eventID: eventId });
}
